import { Modal, Switch } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import cls from 'classnames';

import { LocalImage } from '../../Image';
import { useTranslation } from 'next-i18next';
import styles from './index.module.scss';

import {
  postGetNpcPrivacySetting,
  postUpdateNpcGroupChatSetting,
  postUpdateNpcPlotSetting,
  postUpdatePrivacySetting,
} from '../../../api/npc';
import {
  EGroupChatSetting,
  ENpcPlotGlobalSettingType,
  IGetNpcPrivacySettingResp,
} from '../../../types/npc';
import { EPrivacySetting, WorkspaceNpcInfo } from '../../../types';
import { SimpleModal } from '../SimpleModal';

import closeIcon from '../../../assets/Edit/close.png';
import selectIcon from '../../../assets/Edit/select.svg';
import unselectIcon from '../../../assets/Edit/unselect.svg';
import arrowIcon from '../../../assets/Home/arrow.svg';
import loadingIcon from '../../../assets/Edit/loading.svg';
import loadingWhiteIcon from '../../../assets/Edit/loading-white.svg';
import { isTalkie } from '../../../utils';
import { track } from '../../../track';
import { getNpcStatus } from '../NpcList';
import { useExposeItem } from '../../../hooks';
import { trackWithNpc } from '../NpcTabs';

const PAGE = 'ugc_main_page';

interface INpcPermissionModal {
  npcInfo: WorkspaceNpcInfo;
  show: boolean;
  onCloseModal: () => void;
}

interface IPermissionSelectProps {
  checked: boolean;
  title: string;
  desc?: string;
  handleSelect: () => void;
}

interface IPermissionCardProps {
  title: string;
  settingDesc: string;
  detail: React.ReactNode;
}

interface INotifyCardProps {
  text: string;
  checked: boolean;
  disabled: boolean;
  handleSwitch: (isCheck: boolean) => void;
}

const PermissionSelect = ({ checked, title, desc, handleSelect }: IPermissionSelectProps) => {
  return (
    <div className={styles.select} onClick={handleSelect}>
      <LocalImage src={checked ? selectIcon : unselectIcon} />
      <div className={styles.selectInfo}>
        <div className={styles.selectTitle}>{title}</div>
        {desc && <div className={styles.selectDesc}>{desc}</div>}
      </div>
    </div>
  );
};

const NotifyCard = ({ text, checked, handleSwitch, disabled }: INotifyCardProps) => {
  return (
    <div className={cls(styles.notify, { [styles.notifyDisabled]: disabled })}>
      <div>{text}</div>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(isCheck) => {
          handleSwitch(isCheck);
        }}
      />
    </div>
  );
};

const PermissionCard = ({ title, settingDesc, detail }: IPermissionCardProps) => {
  const [showDetail, setShowDetail] = useState(false);
  return (
    <div className={styles.card}>
      <div
        className={styles.cardInfo}
        onClick={() => {
          setShowDetail((show) => !show);
        }}
      >
        <div className={styles.cardTitle}>{title}</div>
        <div className={styles.cardDesc}>
          {settingDesc}
          <LocalImage src={arrowIcon} className={cls({ [styles.img_right]: showDetail })} />
        </div>
      </div>
      {showDetail && <div className={styles.detail}>{detail}</div>}
    </div>
  );
};

export function NpcPermissionModal({ show, onCloseModal, npcInfo }: INpcPermissionModal) {
  const { t } = useTranslation();
  const [npcSetting, setNpcSetting] = useState<IGetNpcPrivacySettingResp>(
    {} as IGetNpcPrivacySettingResp
  );
  const [initialNpcSetting, setInitialNpcSetting] = useState<IGetNpcPrivacySettingResp>(
    {} as IGetNpcPrivacySettingResp
  );
  const [loading, setLoading] = useState(false);
  const [showGroupChangeModal, setShowGroupChangeModal] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const fetchPrivacySetting = useCallback(async () => {
    if (!npcInfo || !npcInfo.npc_id) return;

    setLoading(true);
    try {
      const res = await postGetNpcPrivacySetting({ npc_id: npcInfo.npc_id });
      setNpcSetting(res);
      setInitialNpcSetting(res);
    } catch (error) {
      // handle error
    }
    setLoading(false);
  }, [npcInfo, setNpcSetting, setLoading]);

  useEffect(() => {
    if (show) {
      fetchPrivacySetting();
    }
  }, [npcInfo, show]);

  const handleSave = useCallback(async () => {
    if (!npcInfo || !npcInfo.npc_id) return;

    try {
      const { privacy_setting, plot_global_settings, group_chat_setting } = npcSetting;
      const {
        privacy_setting: init_privacy_setting,
        plot_global_settings: init_plot_global_settings,
        group_chat_setting: init_group_chat_setting,
      } = initialNpcSetting;

      trackWithNpc(
        npcInfo,
        'permission_setting_popup_click',
        PAGE,
        {
          npc_privacy_status: privacy_setting === EPrivacySetting.Public ? 'public' : 'privacy',
          story_co_create_status:
            plot_global_settings.prohibit_create === false ? 'public' : 'privacy',
          story_co_create_notice_setting:
            plot_global_settings.prohibit_reminder === false ? 'public' : 'privacy',
          group_chat_create_status:
            group_chat_setting === EGroupChatSetting.All ? 'public' : 'privacy',
        },
        'collect'
      );

      const promise: any[] = [];

      privacy_setting !== init_privacy_setting &&
        promise.push(
          postUpdatePrivacySetting({
            npc_id: npcInfo.npc_id,
            privacy_setting,
            edit_version: npcInfo.status_info.edit_version,
          })
        );
      plot_global_settings.prohibit_create !== init_plot_global_settings.prohibit_create &&
        promise.push(
          postUpdateNpcPlotSetting({
            npc_id: npcInfo.npc_id,
            setting_type: ENpcPlotGlobalSettingType.GlobalProhibitCreate,
            is_remove_prohibit: !plot_global_settings.prohibit_create,
          })
        );
      plot_global_settings.prohibit_reminder !== init_plot_global_settings.prohibit_reminder &&
        promise.push(
          postUpdateNpcPlotSetting({
            npc_id: npcInfo.npc_id,
            setting_type: ENpcPlotGlobalSettingType.GlobalProhibitReminder,
            is_remove_prohibit: !plot_global_settings.prohibit_create,
          })
        );
      group_chat_setting !== init_group_chat_setting &&
        promise.push(
          postUpdateNpcGroupChatSetting({
            npc_id: npcInfo.npc_id,
            group_chat_setting,
            edit_version: npcInfo.status_info.edit_version,
          })
        );

      await Promise.allSettled(promise);
    } catch (error) {
      // handle error
    }
    onCloseModal();
  }, [npcInfo, npcSetting, initialNpcSetting]);

  const onExpose = useCallback(() => {
    trackWithNpc(npcInfo, 'permission_setting_popup_view', PAGE, {}, 'collect');
  }, [npcInfo]);

  useExposeItem({
    key: npcInfo.npc_id,
    ref,
    onExpose,
  });

  return (
    <Modal
      classNames={{ content: styles.modal }}
      centered
      destroyOnClose
      open={show}
      onCancel={onCloseModal}
      closeIcon={null}
      width={428}
      footer={
        <div className={styles.permissionBtns}>
          <button className={styles.cancelBtn} onClick={onCloseModal}>
            {t('cancel')}
          </button>
          <button className={styles.saveBtn} onClick={handleSave}>
            {t('save')}
          </button>
        </div>
      }
    >
      <div className={styles.header} ref={ref}>
        {t('permission_setting')}
        <LocalImage src={closeIcon} onClick={onCloseModal} />
      </div>
      <div className={cls(styles.body, { [styles.loadingBody]: loading })}>
        {loading ? (
          <div className={styles.loading}>
            <LocalImage key={'loading'} src={isTalkie ? loadingWhiteIcon : loadingIcon} />
            <p>{t('commonLoading')}</p>
          </div>
        ) : (
          <>
            <div className={styles.selectList}>
              <PermissionSelect
                checked={npcSetting?.privacy_setting === EPrivacySetting.Public}
                title={t('public_chat_available')}
                desc={t('public_chat_available_desc')}
                handleSelect={() => {
                  setNpcSetting({ ...npcSetting, privacy_setting: EPrivacySetting.Public });
                }}
              />
              <PermissionSelect
                checked={
                  npcSetting?.privacy_setting === EPrivacySetting.Private ||
                  npcSetting?.privacy_setting === EPrivacySetting.Unlisted
                }
                title={t('invisibility')}
                desc={t('invisibility_desc')}
                handleSelect={() => {
                  setNpcSetting({ ...npcSetting, privacy_setting: EPrivacySetting.Private });
                }}
              />
            </div>
            <div className={styles.divider} />
            <PermissionCard
              title={t('who_can_create_story')}
              settingDesc={
                npcSetting.plot_global_settings?.prohibit_create
                  ? t('only_yourself')
                  : t('allow_others_to_create')
              }
              detail={
                <>
                  <PermissionSelect
                    checked={npcSetting?.plot_global_settings?.prohibit_create === false}
                    title={t('allow_others_to_create')}
                    desc={t('allow_others_to_create_story_desc')}
                    handleSelect={() => {
                      setNpcSetting({
                        ...npcSetting,
                        plot_global_settings: {
                          ...npcSetting.plot_global_settings,
                          prohibit_create: false,
                        },
                      });
                    }}
                  />
                  <NotifyCard
                    text={t('notify_followers_to_create_story')}
                    checked={npcSetting?.plot_global_settings?.prohibit_reminder === false}
                    disabled={npcSetting?.plot_global_settings?.prohibit_create === true}
                    handleSwitch={(isCheck) => {
                      setNpcSetting({
                        ...npcSetting,
                        plot_global_settings: {
                          ...npcSetting.plot_global_settings,
                          prohibit_reminder: !isCheck,
                        },
                      });
                    }}
                  />
                  <PermissionSelect
                    checked={npcSetting?.plot_global_settings?.prohibit_create === true}
                    title={t('only_yourself')}
                    handleSelect={() => {
                      setNpcSetting({
                        ...npcSetting,
                        plot_global_settings: {
                          ...npcSetting.plot_global_settings,
                          prohibit_create: true,
                        },
                      });
                    }}
                  />
                </>
              }
            />
            <div className={styles.divider} />

            <PermissionCard
              title={t('who_can_create_group_chat')}
              settingDesc={
                npcSetting.group_chat_setting === EGroupChatSetting.All
                  ? t('allow_others_to_create')
                  : t('only_yourself')
              }
              detail={
                <>
                  <PermissionSelect
                    checked={npcSetting?.group_chat_setting === EGroupChatSetting.All}
                    title={t('allow_others_to_create')}
                    desc={t('allow_others_to_create_group_chat_desc')}
                    handleSelect={() => {
                      setNpcSetting({
                        ...npcSetting,
                        group_chat_setting: EGroupChatSetting.All,
                      });
                    }}
                  />
                  <PermissionSelect
                    checked={npcSetting?.group_chat_setting === EGroupChatSetting.SelfOnly}
                    title={t('only_yourself')}
                    handleSelect={() => {
                      setShowGroupChangeModal(true);
                    }}
                  />
                </>
              }
            />
          </>
        )}
      </div>

      <SimpleModal
        show={showGroupChangeModal}
        title={t('change_create_group_chat_permission')}
        desc={t('change_create_group_chat_permission_desc')}
        confirmText={t('confirm')}
        onClose={() => {
          setShowGroupChangeModal(false);
        }}
        onConfirm={() => {
          setNpcSetting({
            ...npcSetting,
            group_chat_setting: EGroupChatSetting.SelfOnly,
          });
        }}
      />
    </Modal>
  );
}
